import { useEffect, useMemo, useState } from "react";
import { ExternalLink, ChevronDown, FileText, Clock, Beaker, Mic2, MapPin, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Stagger, staggerItem } from "@/components/site/Reveal";
import { SectionLabel } from "@/components/site/SectionLabel";
import { PageMeta } from "@/components/site/PageMeta";
import { getSection, listItems } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const STATUS_LABEL = {
    published: { label: "Published", tone: "bg-forest text-paper", icon: FileText },
    forthcoming: { label: "Forthcoming", tone: "bg-brass text-white", icon: Clock },
    under_review: { label: "Under Review", tone: "bg-ink text-paper", icon: Beaker },
};

const AREA_COLORS = ["var(--oxblood)", "var(--brass)", "var(--ocean)", "var(--moss)"];

// Group publications by year, newest first. Blank/unknown years go to "Undated".
function groupByYear(pubs) {
    const buckets = new Map();
    pubs.forEach((p) => {
        const year = (p.year || "").toString().trim() || "Undated";
        if (!buckets.has(year)) buckets.set(year, []);
        buckets.get(year).push(p);
    });
    // Sort: numeric years descending, "Undated" last
    return [...buckets.entries()].sort((a, b) => {
        if (a[0] === "Undated") return 1;
        if (b[0] === "Undated") return -1;
        return parseInt(b[0]) - parseInt(a[0]);
    });
}

// Extract a 2-line summary: prefer explicit `summary`, else first sentences of abstract
function shortSummary(pub) {
    if (pub.summary) return pub.summary;
    const abs = (pub.abstract || "").trim();
    if (!abs) return "";
    // Cap at ~180 chars for a clean 2-line render
    return abs.length > 180 ? abs.slice(0, 177).trimEnd() + "…" : abs;
}

export default function ResearchPage() {
    const [research, setResearch] = useState(null);
    const [pubs, setPubs] = useState([]);
    const [confs, setConfs] = useState([]);

    useEffect(() => {
        getSection("research").then((r) => setResearch(r.data || {}));
        listItems("publications").then(setPubs);
        listItems("conferences").then(setConfs);
    }, []);

    const r = research || {};
    const published = useMemo(() => pubs.filter((p) => (p.status || "published") === "published"), [pubs]);
    const forthcoming = useMemo(() => pubs.filter((p) => p.status === "forthcoming"), [pubs]);
    const underReview = useMemo(() => pubs.filter((p) => p.status === "under_review"), [pubs]);
    const workingPapers = useMemo(() => [...forthcoming, ...underReview], [forthcoming, underReview]);
    const presented = useMemo(() => confs.filter((c) => (c.type || "presented") === "presented"), [confs]);
    const attended = useMemo(() => confs.filter((c) => c.type === "attended"), [confs]);

    const publishedByYear = useMemo(() => groupByYear(published), [published]);

    return (
        <>
            <PageMeta title="Research — Aditya Baheti" description="Research vision, published work, forthcoming publications, manuscripts under review, and conference presentations." path="/research" />

            {/* ============================= HERO ================================= */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 hero-glow" aria-hidden />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-10">
                    <SectionLabel label="Research" />
                    <Reveal>
                        <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.98] tracking-[-0.02em] max-w-4xl">
                            Corporate law that serves the <span className="text-claret">public interest</span>.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <div className="mt-8 flex flex-wrap gap-3 items-center">
                            <a className="link-underline text-sm font-mono uppercase tracking-widest" href="https://orcid.org/0009-0007-7065-0278" target="_blank" rel="noreferrer" data-testid="research-orcid-link">ORCID <ArrowUpRight className="inline h-3 w-3" /></a>
                            <span className="text-muted-foreground">·</span>
                            <a className="link-underline text-sm font-mono uppercase tracking-widest" href="https://papers.ssrn.com/sol3/cf_dev/AbsByAuth.cfm?per_id=7711291" target="_blank" rel="noreferrer" data-testid="research-ssrn-link">SSRN <ArrowUpRight className="inline h-3 w-3" /></a>
                            <span className="text-muted-foreground">·</span>
                            <a className="link-underline text-sm font-mono uppercase tracking-widest text-muted-foreground" href="#google-scholar">Google Scholar</a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ============================ VISION ================================ */}
            <section className="relative overflow-hidden">
                <div className="claret-glow absolute inset-0" aria-hidden />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <SectionLabel label="Research vision" />
                        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                            The intellectual centre of this work.
                        </p>
                    </div>
                    <div className="lg:col-span-8">
                        <Reveal>
                            <p className="font-display text-2xl sm:text-3xl leading-[1.35] tracking-tight max-w-[52ch]">
                                {r.vision || r.agenda}
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ========================= AREAS ==================================== */}
            <section className="bg-secondary/40 border-y border-border/70 py-16 sm:py-20 relative overflow-hidden">
                <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full blur-3xl bg-[hsl(var(--forest)/0.15)]" />
                <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full blur-3xl bg-[hsl(var(--brass)/0.12)]" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionLabel label="Areas of research" />
                    <Reveal>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Four lenses on modern corporate law.</h2>
                    </Reveal>
                    <Stagger className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {(r.areas || r.interests || []).map((area, i) => (
                            <motion.div key={area} variants={staggerItem}>
                                <Card className="relative overflow-hidden p-6 rounded-2xl bg-card border-border/70 h-full">
                                    <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `hsl(${AREA_COLORS[i % AREA_COLORS.length]})` }} />
                                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">§ {String(i + 1).padStart(2, "0")}</p>
                                    <p className="mt-3 font-display text-2xl leading-tight">{area}</p>
                                </Card>
                            </motion.div>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* ==================== PUBLICATIONS (year-grouped grid) ============== */}
            <section id="publications" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 scroll-mt-24">
                <SectionLabel label="Publications" />
                <Reveal>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-3xl">In print.</h2>
                </Reveal>
                <Reveal delay={0.05}>
                    <p className="mt-3 text-muted-foreground max-w-[62ch]">
                        Peer-reviewed articles in law reviews and journals, grouped by year — newest first.
                    </p>
                </Reveal>

                <div className="mt-12 space-y-14" data-testid="research-publications-list">
                    {publishedByYear.map(([year, items]) => (
                        <div key={year}>
                            <div className="flex items-baseline gap-4 mb-6">
                                <span className="font-display text-3xl sm:text-4xl text-forest tracking-tight">{year}</span>
                                <span className="h-px flex-1 bg-border/70" />
                                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                                    {items.length} {items.length === 1 ? "article" : "articles"}
                                </span>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                {items.map((p, i) => (
                                    <Reveal key={p.id} delay={i * 0.04}>
                                        <PublicationCard pub={p} />
                                    </Reveal>
                                ))}
                            </div>
                        </div>
                    ))}
                    {published.length === 0 && <p className="text-sm text-muted-foreground">No publications yet.</p>}
                </div>
            </section>

            {/* ==================== WORKING PAPERS ================================ */}
            <section id="working-papers" className="relative overflow-hidden bg-secondary/40 border-y border-border/70 py-16 sm:py-20 scroll-mt-24">
                <div className="claret-glow absolute inset-0" aria-hidden />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionLabel label="Working papers" />
                    <Reveal>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-3xl">
                            Accepted for publication or currently under peer review.
                        </h2>
                    </Reveal>
                    {workingPapers.length > 0 ? (
                        <div className="mt-10 grid md:grid-cols-2 gap-4">
                            {workingPapers.map((p, i) => (
                                <Reveal key={p.id} delay={i * 0.04}>
                                    <PublicationCard pub={p} />
                                </Reveal>
                            ))}
                        </div>
                    ) : (
                        <p className="mt-6 text-sm text-muted-foreground">No working papers listed at the moment.</p>
                    )}
                </div>
            </section>

            {/* ==================== PRESENTATIONS ================================= */}
            <section id="presentations" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 scroll-mt-24">
                <SectionLabel label="Presentations" />
                <Reveal>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-3xl">Papers presented at conferences.</h2>
                </Reveal>
                <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="research-presented-list">
                    {presented.map((c, i) => (
                        <Reveal key={c.id} delay={i * 0.05}>
                            <ConferenceCard c={c} />
                        </Reveal>
                    ))}
                    {presented.length === 0 && <p className="text-sm text-muted-foreground">No presentations yet.</p>}
                </div>

                {attended.length > 0 && (
                    <div className="mt-14">
                        <SectionLabel label="Conferences attended" />
                        <div className="mt-4 divide-y divide-border/70 border-y border-border/70" data-testid="research-attended-list">
                            {attended.map((c, i) => (
                                <Reveal key={c.id} delay={i * 0.03}>
                                    <div className="py-4 flex flex-wrap items-baseline justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-display text-lg leading-snug">{c.paper_title}</p>
                                            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-1">
                                                {c.conference_name}{c.location ? ` · ${c.location}` : ""}
                                            </p>
                                        </div>
                                        <p className="font-mono text-sm text-muted-foreground">{c.year}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                )}
            </section>
        </>
    );
}

// -----------------------------------------------------------------------------
// PublicationCard — title, venue/year, 2-line summary, "Read more" accordion
// -----------------------------------------------------------------------------
function PublicationCard({ pub }) {
    const [open, setOpen] = useState(false);
    const status = STATUS_LABEL[pub.status || "published"] || STATUS_LABEL.published;
    const StatusIcon = status.icon;
    const summary = shortSummary(pub);
    const hasAbstract = Boolean(pub.abstract);

    return (
        <Card
            className="group h-full p-6 rounded-2xl bg-card border-border/70 hover:shadow-[0_25px_60px_-15px_hsl(var(--ink)/0.20)] transition-shadow flex flex-col"
            data-testid="research-publication-card"
        >
            <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${status.tone}`}>
                    <StatusIcon className="h-3 w-3" /> {status.label}
                </span>
                {pub.coauthors && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        {pub.coauthors}
                    </span>
                )}
                {pub.year && (
                    <span className="ml-auto font-mono text-[11px] uppercase tracking-widest text-forest">
                        {pub.year}
                    </span>
                )}
            </div>

            <p className="mt-4 font-display text-xl sm:text-[22px] leading-snug">{pub.title}</p>

            {(pub.venue || pub.venue_detail) && (
                <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    {pub.venue}{pub.venue_detail ? ` · ${pub.venue_detail}` : ""}
                </p>
            )}

            {summary && !open && (
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                    {summary}
                </p>
            )}

            <div className="mt-auto pt-4 flex items-center gap-3 flex-wrap">
                {hasAbstract && (
                    <Collapsible open={open} onOpenChange={setOpen} className="w-full">
                        <CollapsibleTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="px-2 -ml-2 text-forest hover:text-forest hover:bg-forest/[0.06]"
                                data-testid="research-publication-abstract-toggle"
                            >
                                <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${open ? "rotate-180" : ""}`} />
                                {open ? "Show less" : "Read more"}
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="mt-2 pl-1 border-l-2 border-forest/40 pl-4">
                                <p className="text-sm leading-relaxed text-foreground/80">
                                    {pub.abstract}
                                </p>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                )}
                {pub.url && (
                    <a
                        href={pub.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="ml-auto inline-flex items-center gap-1 text-sm link-underline"
                        data-testid="research-publication-link"
                    >
                        SSRN <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                )}
            </div>
        </Card>
    );
}

function ConferenceCard({ c }) {
    return (
        <Card className="h-full p-6 rounded-2xl border-border/70 relative bg-card hover:shadow-[0_25px_60px_-15px_hsl(var(--ink)/0.20)] transition-shadow">
            <div className="flex items-center gap-2">
                <Mic2 className="h-4 w-4 text-oxblood" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-copper">Presented · {c.year}</span>
            </div>
            <p className="mt-3 font-display text-xl leading-snug">{c.paper_title}</p>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground flex items-start gap-1">
                <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{c.conference_name}{c.location ? ` · ${c.location}` : ""}</span>
            </p>
            {c.url && (
                <a href={c.url} target="_blank" rel="noreferrer noopener" className="mt-4 inline-flex items-center gap-1 text-sm link-underline">
                    Details <ExternalLink className="h-3.5 w-3.5" />
                </a>
            )}
        </Card>
    );
}
